import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Plus, X, User, Lock, Clock, Key, Book, Code, ChevronRight, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  const [profile, setProfile] = useState({
    email: '',
    name: '',
    screen_name: '',
    mobile_number: '',
    minutes: 0
  });
  
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [dictionary, setDictionary] = useState([]);
  const [defaultDictionary, setDefaultDictionary] = useState([]);
  const [excludedDefaults, setExcludedDefaults] = useState([]);
  const [newWord, setNewWord] = useState('');
  const [wordToDelete, setWordToDelete] = useState(null);
  const [isDefaultWord, setIsDefaultWord] = useState(false);
  
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKey, setShowNewKey] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchDictionary();
    fetchDefaultDictionary();
    fetchExcludedDefaults();
    fetchPackages();
    fetchApiKeys();
    
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId) {
      handlePaymentReturn(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment cancelled');
    }
  }, [searchParams]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      setProfile({
        email: response.data.email || '',
        name: response.data.name || '',
        screen_name: response.data.screen_name || '',
        mobile_number: response.data.mobile_number || '',
        minutes: response.data.minutes || 0
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const fetchDictionary = async () => {
    try {
      const response = await api.get('/settings/dictionary');
      setDictionary(response.data.words || []);
    } catch (error) {
      console.error('Failed to fetch dictionary:', error);
    }
  };

  const fetchDefaultDictionary = async () => {
    try {
      const response = await api.get('/settings/dictionary/defaults');
      setDefaultDictionary(response.data.words || []);
    } catch (error) {
      console.error('Failed to fetch default dictionary:', error);
    }
  };

  const fetchExcludedDefaults = async () => {
    try {
      const response = await api.get('/settings/dictionary/excluded');
      setExcludedDefaults(response.data.words || []);
    } catch (error) {
      console.error('Failed to fetch excluded defaults:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get('/payments/packages');
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const updateProfile = async () => {
    try {
      await api.put('/user/profile', {
        name: profile.name,
        screen_name: profile.screen_name,
        mobile_number: profile.mobile_number
      });
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwords.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.post('/user/change-password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const addWord = async (e) => {
    e.preventDefault();
    if (!newWord.trim()) return;

    try {
      await api.post('/settings/dictionary', { word: newWord.trim() });
      toast.success('Word added to dictionary');
      setNewWord('');
      fetchDictionary();
    } catch (error) {
      toast.error('Failed to add word');
    }
  };

  const removeWord = async (word) => {
    try {
      if (isDefaultWord) {
        await api.post('/settings/dictionary/exclude', { word });
        toast.success('Default word excluded');
        fetchExcludedDefaults();
      } else {
        await api.delete(`/settings/dictionary/${word}`);
        toast.success('Word removed from dictionary');
        fetchDictionary();
      }
      setWordToDelete(null);
      setIsDefaultWord(false);
    } catch (error) {
      toast.error('Failed to remove word');
    }
  };

  const restoreDefaultWord = async (word) => {
    try {
      await api.delete(`/settings/dictionary/exclude/${word}`);
      toast.success('Default word restored');
      fetchExcludedDefaults();
    } catch (error) {
      toast.error('Failed to restore word');
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await api.get('/api-keys');
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys');
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }
    
    try {
      const response = await api.post('/api-keys', { name: newKeyName });
      setShowNewKey(response.data);
      setNewKeyName('');
      fetchApiKeys();
      toast.success('API key created successfully');
    } catch (error) {
      toast.error('Failed to create API key');
    }
  };

  const deleteApiKey = async (keyId) => {
    try {
      await api.delete(`/api-keys/${keyId}`);
      toast.success('API key deleted');
      fetchApiKeys();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const toggleApiKey = async (keyId) => {
    try {
      await api.patch(`/api-keys/${keyId}/toggle`);
      toast.success('API key status updated');
      fetchApiKeys();
    } catch (error) {
      toast.error('Failed to update API key');
    }
  };

  const purchaseMinutes = async (minutes) => {
    try {
      setSelectedPackage(minutes);
      setIsProcessingPayment(true);
      
      const originUrl = window.location.origin;
      const response = await api.post('/payments/checkout/session', {
        minutes,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setIsProcessingPayment(false);
      setSelectedPackage(null);
    }
  };

  const handlePaymentReturn = async (sessionId) => {
    setIsProcessingPayment(true);
    toast.info('Verifying payment...');
    
    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        toast.error('Payment verification timed out. Please refresh the page.');
        setIsProcessingPayment(false);
        return;
      }
      
      try {
        const response = await api.get(`/payments/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'completed') {
          toast.success(`Payment successful! ${response.data.minutes} minutes added to your account.`);
          fetchProfile();
          refreshUser();
          setIsProcessingPayment(false);
          window.history.replaceState({}, document.title, '/settings');
          return;
        } else if (response.data.payment_status === 'expired') {
          toast.error('Payment session expired');
          setIsProcessingPayment(false);
          return;
        }
        
        attempts++;
        setTimeout(pollStatus, pollInterval);
      } catch (error) {
        toast.error('Error verifying payment');
        setIsProcessingPayment(false);
      }
    };
    
    pollStatus();
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold">Settings</h1>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Profile
            </CardTitle>
            <CardDescription>Manage your account information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                value={profile.email} 
                disabled 
                className="bg-muted cursor-not-allowed" 
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={profile.name || ''}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="screen_name">Screen Name</Label>
              <Input
                id="screen_name"
                value={profile.screen_name || ''}
                onChange={(e) => setProfile({ ...profile, screen_name: e.target.value })}
                placeholder="Your display name (shown to other users)"
                maxLength={30}
              />
              <p className="text-xs text-muted-foreground">This is how other users will see you in the shared library.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number</Label>
              <PhoneInput
                country={'us'}
                value={profile.mobile_number || ''}
                onChange={(value) => setProfile({ ...profile, mobile_number: value })}
                inputStyle={{
                  width: '100%',
                  height: '40px',
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.375rem',
                  color: 'hsl(var(--foreground))',
                }}
                buttonStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.375rem 0 0 0.375rem',
                }}
                dropdownStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--foreground))',
                }}
                containerClass="phone-input-container"
              />
            </div>

            <Button onClick={updateProfile}>Save Profile</Button>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={changePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwords.current_password}
                  onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwords.new_password}
                  onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwords.confirm_password}
                  onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit">Change Password</Button>
            </form>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Processing Minutes
            </CardTitle>
            <CardDescription>
              You have <span className="font-bold text-lg">{profile.minutes.toFixed(1)}</span> minutes remaining
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Purchase additional minutes to process more tracks. Minutes are deducted based on song length.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {packages.map((pkg) => (
                <Card
                  key={pkg.minutes}
                  className="cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => !isProcessingPayment && purchaseMinutes(pkg.minutes)}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="text-2xl font-bold">{pkg.minutes} min</div>
                    <div className="text-xl text-blue-600 font-semibold mt-2">
                      ${pkg.price.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      ${pkg.price_per_minute.toFixed(2)}/min
                    </div>
                    {selectedPackage === pkg.minutes && isProcessingPayment && (
                      <div className="mt-2 text-xs text-blue-600">Processing...</div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={!!wordToDelete} onOpenChange={() => { setWordToDelete(null); setIsDefaultWord(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isDefaultWord ? 'Exclude Default Word' : 'Remove Word'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isDefaultWord 
                ? `Are you sure you want to exclude "${wordToDelete}" from the default filter? This word will no longer be censored. You can restore it later.`
                : `Are you sure you want to remove "${wordToDelete}" from your custom dictionary?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeWord(wordToDelete)}>
              {isDefaultWord ? 'Exclude' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
